import { CurrentTimerText } from "@jugl-web/domain-resources/task-time-tracking/components/CurrentTimerText";
import { StopTimerButton } from "@jugl-web/domain-resources/task-time-tracking/components/StopTimerButton";
import { TimerTextWrapper } from "@jugl-web/domain-resources/task-time-tracking/components/TimerTextWrapper";
import { useActiveTimeTrackingContext } from "@jugl-web/domain-resources/task-time-tracking/providers/ActiveTimeTrackingProvider";
import { useTaskObjectNaming } from "@jugl-web/domain-resources/tasks/hooks/useTaskObjectNaming";
import { NavbarButton } from "@web-src/features/app/components/MainNavBar/components/NavbarButton";
import { TimeTrackedPopoverContent } from "@web-src/features/tasks/TimeTrackedPopoverContent";
import {
  TimeTrackingDialogsFlow,
  TimeTrackingDialogsFlowHandle,
} from "@web-src/features/tasks/TimeTrackingDialogsFlow";
import { useNavigateToTaskDetailsPage } from "@web-src/hooks/useNavigateToTaskDetailsPage";
import { useSingletonPopover } from "@web-src/modules/common/providers/SingletonPopoverProvider";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { useRef } from "react";

export const TaskTimeTrackerNavbarButton = () => {
  const { openPopover, closePopover, isPopoverOpen } = useSingletonPopover();

  const { entityId } = useEntitySelectedProvider();

  const navigateToTaskDetailsPage = useNavigateToTaskDetailsPage();

  const { activeTimeLog, isLoading, stopActiveTimer } =
    useActiveTimeTrackingContext();

  const timeTrackingDialogsFlowHandleRef =
    useRef<TimeTrackingDialogsFlowHandle | null>(null);

  const taskObjectNaming = useTaskObjectNaming({
    entityId,
    boardId: activeTimeLog?.boardId || null,
  });

  if (!activeTimeLog) {
    return null;
  }

  return (
    <>
      <NavbarButton
        isActive={isPopoverOpen}
        className="gap-2.5"
        onClick={(event) => {
          openPopover({
            triggerElement: event.currentTarget,
            Component: TimeTrackedPopoverContent,
            componentProps: {
              entityId,
              taskId: activeTimeLog.taskId,
              header: {
                type: "with-task-name",
                taskObjectNaming,
                taskName: activeTimeLog.taskName,
                onNavigateToTask: () =>
                  navigateToTaskDetailsPage({
                    taskId: activeTimeLog.taskId,
                    customTasksSource: activeTimeLog.boardId
                      ? { type: "boardTasks", boardId: activeTimeLog.boardId }
                      : undefined,
                  }),
              },
              withHistory: true,
              onAddTimeLog: () =>
                timeTrackingDialogsFlowHandleRef.current?.onAddTimeLog(),
              onEditTimeLog: (timeLog) =>
                timeTrackingDialogsFlowHandleRef.current?.onEditTimeLog(
                  timeLog
                ),
              onDeleteTimeLog: (timeLog) =>
                timeTrackingDialogsFlowHandleRef.current?.onDeleteTimeLog(
                  timeLog
                ),
              onShowAll: () => {
                navigateToTaskDetailsPage({
                  taskId: activeTimeLog.taskId,
                  customTasksSource: activeTimeLog.boardId
                    ? { type: "boardTasks", boardId: activeTimeLog.boardId }
                    : undefined,
                  initialTabId: "time-logs",
                });
              },
              onClose: closePopover,
            },
            popoverClassName: "overflow-hidden rounded-2xl",
          });
        }}
      >
        <StopTimerButton
          className="h-6 w-6 shrink-0"
          isDisabled={isLoading}
          onClick={async (event) => {
            event.stopPropagation();
            await stopActiveTimer();
          }}
        />
        <TimerTextWrapper>
          <CurrentTimerText startDate={activeTimeLog.startDate} />
        </TimerTextWrapper>
      </NavbarButton>
      <TimeTrackingDialogsFlow
        ref={timeTrackingDialogsFlowHandleRef}
        taskId={activeTimeLog.taskId}
        taskName={activeTimeLog.taskName}
        boardId={activeTimeLog.boardId}
      />
    </>
  );
};
